import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, RotateCcw, Save, Search, Settings, Upload } from 'lucide-react';
import * as hr from '../api';
import { useHR, useLookup } from '../HRContext';
import { errorText } from '../../utils';
import StatusPill from '../components/StatusPill';
import { EMPLOYMENT_TYPES, fmtDate, initials, titleCase } from '../format';
import Icon from '../Icon';
import Loader from '../components/Loader';
import Picker from '../components/Picker';
import { DateInput, Select } from '../components/Controls';
import { FileChip, FilePicker, openStored } from '../components/Files';
import { CHECKLIST_RULE } from '../fileRules';
import Pagination from '../../components/requisition/Pagination';

// Employee Document Checklist — ported from Stephanet (custom/EmployeeDocChecklist.jsx). The required documents are
// configurable (Document Settings, table doc_requirements); each employee's progress on each one — submitted, signed,
// verified, N/A, expiry, remarks and files — is recorded in Document Update (table employee_doc_statuses). A document the
// employee record already holds (a TIN, bank account, uploaded CV …) counts as submitted "from profile".
// The matrix is computed, filtered and paged on the server (backend/app/services/doc_checklist_service.py).

// cell state -> [label, symbol, css]
export const STATES = {
  not_submitted: ['Not Submitted', '○', 'not'], submitted: ['Submitted', '▸', 'sub'], signed: ['Signed', '✎', 'sig'],
  submitted_signed: ['Submitted & Signed', '✓', 'ss'], half: ['Half Complete', '◐', 'half'], pending: ['Pending Verification', '⏳', 'pend'],
  verified: ['Verified', '✔', 'ver'], complete: ['Fully Complete', '★', 'full'], expired: ['Expired', '✕', 'exp'], na: ['N/A', '—', 'na'],
};
const STATUS_TONE = { 'Fully Complete': 'good', 'Partially Complete': 'info', 'Pending Verification': 'warn', Incomplete: 'bad', 'Not Started': 'muted', 'Expired Documents': 'bad' };
const STATUSES = ['Fully Complete', 'Partially Complete', 'Incomplete', 'Pending Verification', 'Expired Documents', 'Not Started', 'Missing Documents'];
const QUICK = [['missing', 'Missing Only'], ['half', 'Half Complete'], ['pending', 'Pending Verification'], ['full', 'Fully Complete'], ['expired', 'Expired'],
  ['expiring', 'Expiring Soon'], ['partial', 'In Progress'], ['new', 'New Employees'], ['active', 'Active Employees']];
const Cell = ({ c, compact }) => {
  const [label, sym, css] = STATES[c?.state] || STATES.not_submitted;
  return <span className={`dc-cell ${css}`} title={c?.from_profile ? `${label} (from employee profile)` : label}><i>{sym}</i>{!compact && <small>{label}</small>}</span>;
};
const needs = r => [r.needs_upload && 'Upload', r.needs_sign && 'Sign', r.needs_verify && 'Verify', r.has_expiry && 'Expiry'].filter(Boolean);
const pageNote = (page, total) => `Page ${Math.floor(page.skip / page.limit) + 1} of ${Math.max(1, Math.ceil(total / page.limit))}`;

export default function EmployeeDocChecklist() {
  const { canManage, notify, confirm } = useHR();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') || 'checklist';
  const go = (v, extra = {}) => setParams({ ...(v !== 'checklist' ? { view: v } : {}), ...extra });
  const props = { go, notify, confirm, params, canEdit: canManage };
  if (view === 'update') return <DocUpdate {...props} />;
  if (view === 'settings') return <DocSettings {...props} />;
  return <Checklist {...props} />;
}

// ---------------------------------------------------------------- checklist matrix (paged on the server)
function Checklist({ go, canEdit }) {
  const [f, setF] = useState({ dept: '', site: '', type: '', status: '' });
  const [q, setQ] = useState('');
  const [search, setSearch] = useState(''); // applied only when Search is clicked
  const [quick, setQuick] = useState('');
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const departments = useLookup('departments') || [];
  const sites = useLookup('sites') || [];
  const firstPage = () => setPage(p => (p.skip ? { ...p, skip: 0 } : p));
  const set = (k, v) => { setF(o => ({ ...o, [k]: v })); firstPage(); };
  const pickQuick = k => { setQuick(k); firstPage(); };
  const params = { search: search || undefined, department_id: f.dept || undefined, site_id: f.site || undefined, employment_type: f.type || undefined,
    status: f.status || undefined, quick: quick || undefined };
  const key = JSON.stringify(params);
  const latest = useRef(0);
  const load = () => {
    const ticket = ++latest.current;
    setError(''); setLoading(true);
    hr.documentChecklistPage({ ...params, skip: page.skip, limit: page.limit })
      .then(r => { if (ticket === latest.current) setData(r); })
      .catch(err => { if (ticket === latest.current) setError(errorText(err)); })
      .finally(() => { if (ticket === latest.current) setLoading(false); });
  };
  useEffect(load, [key, page.skip, page.limit]); // eslint-disable-line react-hooks/exhaustive-deps
  const runSearch = ev => {
    ev?.preventDefault();
    const term = q.trim();
    if (term === search && !page.skip) load(); else { setSearch(term); firstPage(); }
  };

  if (error && !data) return <div className="error">{error}</div>;
  if (!data) return <Loader title="Loading document checklist…" subtitle="Checking documents on file" />;
  const shown = data.items || [];
  const total = data.total || 0;
  const sm = data.summary || {};
  const reqs = data.requirements || [];
  const groups = data.groups || {};
  const grouped = Object.keys(groups).map(g => [g, reqs.filter(r => r.group === g)]).filter(([, rs]) => rs.length);
  // the cards count the whole workforce (server summary); clicking one filters by it
  const kpis = [
    ['Total Employees', sm.total ?? 0, 'blue', 'users', ''], ['Fully Compliant', sm.fully_compliant ?? 0, 'green', 'circle-check', 'full'],
    ['Partially Complete', sm.partially_complete ?? 0, 'indigo', 'loader', 'partial'], ['Missing Documents', sm.missing_documents ?? 0, 'amber', 'file-warning', 'missing'],
    ['Pending Verification', sm.pending_verification ?? 0, 'orange', 'hourglass', 'pending'], ['Expired Documents', sm.expired_documents ?? 0, 'red', 'circle-x', 'expired'],
  ];
  return (
    <div className="dc">
      <div className="e360-head dc-head">
        <div className="e360-head-left"><span className="back-label">{sm.total ?? 0} employees across all sites · {reqs.length} required documents</span></div>
        <div className="e360-head-actions">
          <button className="btn" onClick={() => go('settings')}><Settings size={15} /> Document Settings</button>
          {canEdit && <button className="btn primary" onClick={() => go('update')}><Upload size={15} /> Document Update</button>}
        </div>
      </div>

      <div className="dc-kpis">
        {kpis.map(([l, v, c, i, k]) => (
          <button key={l} type="button" className={`em-kpi lc-${c} ${k && quick === k ? 'on' : ''}`} onClick={() => pickQuick(k && quick !== k ? k : '')}>
            <span className="lc-icon"><Icon name={i} size={19} /></span><div><b>{v}</b><span>{l}</span></div>
          </button>
        ))}
      </div>

      <div className="panel me-table-panel dc-panel">
        <div className="toolbar em-filters">
          <form className="me-search" onSubmit={runSearch}>
            <div className="search em-search"><Search size={16} /><input placeholder="Search employee, ID, document, department…" value={q} onChange={ev => setQ(ev.target.value)} /></div>
            <button type="submit" className="btn primary" disabled={loading}><Search size={15} /> Search</button>
          </form>
          <Select value={f.dept} onChange={ev => set('dept', ev.target.value)}><option value="">All Departments</option>{departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</Select>
          <Select value={f.site} onChange={ev => set('site', ev.target.value)}><option value="">All Sites</option>{sites.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</Select>
          <Select value={f.type} onChange={ev => set('type', ev.target.value)}><option value="">All Types</option>{EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{titleCase(t)}</option>)}</Select>
          <Select value={f.status} onChange={ev => set('status', ev.target.value)}>
            <option value="">All Statuses</option>{STATUSES.map(s => <option key={s}>{s}</option>)}
          </Select>
          <div className="spacer" /><span className="muted small">{total} employee{total === 1 ? '' : 's'}</span>
        </div>
        <div className="chips dc-chips">
          {QUICK.map(([k, l]) => <button key={k} type="button" className={`chip ${quick === k ? 'active' : ''}`} onClick={() => pickQuick(quick === k ? '' : k)}>{l}</button>)}
        </div>

        {/* the matrix is wider than the screen, so the spinner takes its place rather than sitting in a row */}
        {loading && <Loader title="Loading document checklist…" subtitle={pageNote(page, total)} />}
        <div className="table-wrap register-wrap dc-wrap" style={loading ? { display: 'none' } : undefined}>
          <table className="dc-table">
            <thead>
              <tr>
                <th rowSpan={2} className="sticky c1">EMP ID</th><th rowSpan={2} className="sticky c2">Name</th><th rowSpan={2}>Grade</th><th rowSpan={2}>Department</th>
                <th rowSpan={2}>Job Title</th><th rowSpan={2}>Site</th><th rowSpan={2}>Type</th><th rowSpan={2}>Date Hired</th><th rowSpan={2}>Status</th><th rowSpan={2}>Comp %</th>
                {grouped.map(([g, rs]) => <th key={g} colSpan={rs.length} className={`dc-group g${g}`}>{groups[g]}</th>)}
              </tr>
              <tr>
                {grouped.flatMap(([, rs]) => rs.map(r => (
                  <th key={r.id} className="dc-doc" title={`${r.code} · ${r.type}${needs(r).length ? ` · ${needs(r).join(', ')}` : ''}`}>
                    <span>{r.name}</span><em className={r.mandatory ? 'req' : 'opt'}>{r.mandatory ? 'REQ' : 'OPT'}</em>
                  </th>
                )))}
              </tr>
            </thead>
            <tbody>
              {shown.map(e => (
                <tr key={e.id}>
                  <td className="sticky c1 mono">{e.employee_no}</td><td className="sticky c2"><b>{e.full_name}</b></td><td>{e.grade || '—'}</td>
                  <td>{e.department || '—'}</td><td>{e.job_title || '—'}</td><td>{e.site || '—'}</td><td>{e.type || '—'}</td><td>{e.hire_date ? fmtDate(e.hire_date) : '—'}</td>
                  <td><span className={`pill tone-${STATUS_TONE[e.status]}`}>{e.status}</span></td>
                  <td><div className="dc-comp"><b>{e.comp}%</b><span><i style={{ width: `${e.comp}%` }} /></span></div></td>
                  {grouped.flatMap(([, rs]) => rs).map(r => (
                    <td key={r.id} className={canEdit ? 'clickable' : ''} onClick={() => canEdit && go('update', { id: String(e.id), doc: String(r.id) })}>
                      <Cell c={e.cells[r.id]} />
                    </td>
                  ))}
                </tr>
              ))}
              {!shown.length && <tr><td colSpan={10 + reqs.length} className="empty">No employees match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
        {error && <div className="error">{error}</div>}
        <Pagination total={total} skip={page.skip} limit={page.limit} onChange={setPage} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- document update (one employee)
function DocUpdate({ go, notify, confirm, params, canEdit }) {
  const id = params.get('id') || '';
  const focus = params.get('doc');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const load = () => (id ? hr.employeeDocChecklist(id).then(setData).catch(err => setError(errorText(err))) : Promise.resolve(setData(null)));
  useEffect(() => { setData(null); setError(''); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const loaded = !!data;
  useEffect(() => { if (focus && loaded) setTimeout(() => document.getElementById(`dc-doc-${focus}`)?.scrollIntoView({ block: 'center' }), 200); }, [focus, loaded]);

  const emp = data?.employee;
  const save = async (r, body) => {
    setBusy(r.id);
    try { const res = await hr.updateDocChecklistCell(emp.id, r.id, body); await load(); return res; }
    catch (err) { notify(errorText(err), 'bad'); return null; }
    finally { setBusy(null); }
  };
  const upload = async (r, picked) => {
    setBusy(r.id);
    try {
      for (const file of picked) await hr.uploadChecklistFile(emp.id, r.id, file);
      notify(`${r.name} uploaded`);
      await load();
    } catch (err) { notify(errorText(err), 'bad'); }
    setBusy(null);
  };
  const removeFile = async file => {
    if (!(await confirm({ title: 'Delete File', message: `Delete ${file.name}?`, detail: 'The file is removed from the server. This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return;
    try { await hr.deleteChecklistFile(file.id); notify('File deleted'); await load(); } catch (err) { notify(errorText(err), 'bad'); }
  };
  const pick = v => go('update', v ? { id: v } : {});

  return (
    <div className="dc">
      <div className="e360-head dc-head">
        <div className="e360-head-left">
          <button className="back-btn" title="Back to Checklist" onClick={() => go('checklist')}><ArrowLeft size={17} /></button>
          <span className="back-label">Record document submissions, signatures, uploads & verification</span>
          <Picker className="xp-switch" value={id} onChange={pick} placeholder="Search / select employee…" />
        </div>
      </div>
      {!id && <div className="panel empty">Select an employee above to view and update their document checklist.</div>}
      {id && error && <div className="error">{error}</div>}
      {id && !data && !error && <Loader title="Loading checklist…" subtitle="Checking this employee's documents" />}
      {emp && (
        <>
          <div className="dc-kpis">
            {[['Completion', `${emp.comp}%`, 'blue', 'gauge'], ['Status', emp.status, 'indigo', 'clipboard-check'], ['Missing (mandatory)', emp.missing.length, 'amber', 'file-warning'],
              ['Pending Verification', emp.pending.length, 'orange', 'hourglass'], ['Expired', emp.expired.length, 'red', 'circle-x'], ['Expiring Soon', emp.expiring.length, 'yellow', 'calendar-clock']]
              .map(([l, v, c, i]) => <div key={l} className={`em-kpi lc-${c}`}><span className="lc-icon"><Icon name={i} size={19} /></span><div><b>{v}</b><span>{l}</span></div></div>)}
          </div>
          <div className="panel dc-emp">
            <span className="emp-avatar">{initials(emp.full_name)}</span>
            <div><b>{emp.full_name}</b><small>{[emp.employee_no, emp.job_title, emp.department, emp.site, emp.type].filter(Boolean).join(' · ')}</small></div>
            <StatusPill value={emp.emp_status} />
          </div>
          {Object.entries(data.groups).map(([g, title]) => {
            const rs = data.requirements.filter(r => r.group === g);
            if (!rs.length) return null;
            return (
              <div key={g} className="panel dc-group-panel">
                <h3 className="panel-title"><span className={`dc-gtag g${g}`}>{g}</span> {title}
                  <span className="muted small">{rs.filter(r => ['complete', 'verified'].includes(emp.cells[r.id].state)).length} / {rs.filter(r => emp.cells[r.id].state !== 'na').length} complete</span></h3>
                <div className="table-wrap">
                  <table className="dc-update">
                    <thead><tr><th>Document</th><th>Status</th><th>Submitted</th><th>Signed</th><th>Verified</th><th>Expiry</th><th>File</th><th>N/A</th><th>Remarks</th></tr></thead>
                    <tbody>
                      {rs.map(r => {
                        const c = emp.cells[r.id];
                        const t = r.type;
                        const showSub = t !== 'Signature Only';
                        const showSig = r.needs_sign || ['Signature Only', 'Submission & Signature'].includes(t);
                        const showVer = r.needs_verify || t === 'Verification Only';
                        const files = c.files || [];
                        return (
                          <tr key={r.id} id={`dc-doc-${r.id}`} className={`${String(r.id) === focus ? 'focus' : ''} ${busy === r.id ? 'busy' : ''}`}>
                            <td className="dc-docname"><b>{r.name}</b><small><em className={r.mandatory ? 'req' : 'opt'}>{r.mandatory ? 'REQ' : 'OPT'}</em> {r.code} · {t}{needs(r).length ? ` · ${needs(r).join(', ')}` : ''}</small></td>
                            <td><Cell c={c} />{c.from_profile && <small className="dc-profile">from profile</small>}</td>
                            <td>{showSub ? <label className="dc-check"><input type="checkbox" checked={!!c.submitted} disabled={!canEdit || (c.from_profile && !c.status_id)} onChange={ev => save(r, { submitted: ev.target.checked })} /><small>{c.submitted_on ? fmtDate(c.submitted_on) : (c.from_profile ? 'on profile' : '')}</small></label> : <span className="muted">—</span>}</td>
                            <td>{showSig ? <label className="dc-check"><input type="checkbox" checked={!!c.signed} disabled={!canEdit} onChange={ev => save(r, { signed: ev.target.checked })} /><small>{c.signed_on ? fmtDate(c.signed_on) : ''}</small></label> : <span className="muted">—</span>}</td>
                            <td>{showVer ? <label className="dc-check"><input type="checkbox" checked={!!c.verified} disabled={!canEdit} onChange={ev => save(r, { verified: ev.target.checked })} /><small>{c.verified_by ? `${c.verified_by} · ${fmtDate(c.verified_on)}` : ''}</small></label> : <span className="muted">—</span>}</td>
                            <td>{r.has_expiry ? (
                              <div className="dc-expiry">
                                <DateInput value={c.status_id ? (c.expiry || '') : ''} disabled={!canEdit} onChange={ev => ev.target.value !== (c.expiry || '') && save(r, { expiry_date: ev.target.value || null })} />
                                {c.days_left !== null && c.days_left !== undefined && <small className={`xp-hint ${c.days_left < 0 ? 'bad' : c.days_left <= Math.max(r.reminder_days, 30) ? 'warn' : 'ok'}`}>{c.days_left < 0 ? `Expired ${-c.days_left}d ago` : `${c.days_left} days left`}</small>}
                              </div>
                            ) : <span className="muted">—</span>}</td>
                            <td className="dc-files">
                              {files.map(file => <FileChip key={file.id} name={file.name} size={file.size} onOpen={() => openStored(() => hr.checklistFileBlob(file.id))} onRemove={canEdit ? () => removeFile(file) : undefined} />)}
                              {canEdit && (r.needs_upload || files.length > 0) ? <FilePicker rule={CHECKLIST_RULE} multiple onPick={picked => upload(r, picked)} label={files.length ? 'Add file' : 'Upload'} /> : !files.length && <span className="muted">—</span>}
                            </td>
                            <td><input type="checkbox" checked={!!c.na && c.state === 'na'} disabled={!canEdit} onChange={ev => save(r, { not_applicable: ev.target.checked })} /></td>
                            <td><input className="dc-remarks" defaultValue={c.remarks || ''} key={`${c.status_id}-${c.remarks}`} placeholder="Remarks" disabled={!canEdit}
                                       onBlur={ev => ev.target.value !== (c.remarks || '') && save(r, { remarks: ev.target.value })} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- requirement settings
const APPLY = [['emp_types', 'Emp Types'], ['departments', 'Depts'], ['job_titles', 'Job Titles'], ['sites', 'Sites'], ['grades', 'Grades'], ['categories', 'Categories']];
const FLAGS = [['mandatory', 'Mand.'], ['needs_upload', 'Upload'], ['needs_sign', 'Sign'], ['needs_verify', 'Verify'], ['has_expiry', 'Expiry']];
const SAVED = ['code', 'name', 'doc_group', 'submission_type', 'mandatory', 'needs_upload', 'needs_sign', 'needs_verify', 'has_expiry',
  'emp_types', 'departments', 'job_titles', 'sites', 'grades', 'categories', 'reminder_days', 'sort_order', 'active'];
function DocSettings({ go, notify, confirm, canEdit }) {
  const [meta, setMeta] = useState(null); // { groups, types }
  const [rows, setRows] = useState(null);
  const [orig, setOrig] = useState({});
  const [busy, setBusy] = useState(false);
  const fetchRows = () => hr.listDocRequirements().then(r => {
    const list = [...r.items].sort((a, b) => a.doc_group.localeCompare(b.doc_group) || (a.sort_order || 0) - (b.sort_order || 0));
    setMeta({ groups: r.groups, types: r.submission_types });
    setRows(list); setOrig(Object.fromEntries(list.map(x => [x.id, JSON.stringify(x)])));
  }).catch(e => notify(errorText(e), 'bad'));
  useEffect(() => { fetchRows(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = useMemo(() => (rows || []).filter(r => !r.id || orig[r.id] !== JSON.stringify(r)), [rows, orig]);
  if (!rows) return <Loader title="Loading requirements…" subtitle="Document settings" />;
  const upd = (i, k, v) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const add = () => setRows(rs => [...rs, { code: '', name: '', doc_group: 'A', submission_type: 'Submission Only', mandatory: true, needs_upload: true, needs_sign: false,
    needs_verify: false, has_expiry: false, reminder_days: 0, active: true, sort_order: rs.length }]);
  const save = async () => {
    setBusy(true);
    try {
      for (const r of dirty) {
        if (!r.code || !r.name) throw new Error('Every document needs a code and a name');
        const body = Object.fromEntries(SAVED.map(k => [k, r[k] === '' ? null : r[k]]));
        body.code = String(body.code).toUpperCase().trim();
        if (r.id) await hr.updateDocRequirement(r.id, body); else await hr.createDocRequirement(body);
      }
      notify(`${dirty.length} requirement${dirty.length === 1 ? '' : 's'} saved`);
      await fetchRows();
    } catch (err) { notify(errorText(err), 'bad'); }
    setBusy(false);
  };
  const reset = async () => {
    if (!(await confirm({ title: 'Reset to Defaults', message: 'Restore the 50 standard documents to their default settings?', detail: 'Documents you added are kept.', confirmLabel: 'Reset' }))) return;
    try { await hr.resetDocRequirements(); notify('Standard requirements restored'); await fetchRows(); } catch (err) { notify(errorText(err), 'bad'); }
  };
  return (
    <div className="dc">
      <div className="e360-head dc-head">
        <div className="e360-head-left">
          <button className="back-btn" title="Back to Checklist" onClick={() => go('checklist')}><ArrowLeft size={17} /></button>
          <span className="back-label">Configure which documents apply to which employees — no hardcoding</span>
        </div>
        <div className="e360-head-actions">
          {dirty.length > 0 && <span className="xp-dirty">{dirty.length} unsaved</span>}
          {canEdit && <button className="btn" onClick={add}><Plus size={15} /> Add Document</button>}
          {canEdit && <button className="btn" onClick={reset}><RotateCcw size={15} /> Reset to Defaults</button>}
          {canEdit && <button className="btn primary" disabled={!dirty.length || busy} onClick={save}><Save size={15} /> {busy ? 'Saving…' : 'Save'}</button>}
        </div>
      </div>
      <div className="xp-note info dc-note">
        {rows.filter(r => r.active !== false).length} active requirements · applicability resolves per employee from Employment Type, Department, Job Title, Site, Grade &amp; Category.
        Leave a list blank for “All”, or enter comma-separated values (Categories also accept “Expatriate” or “Local”).
      </div>
      <div className="panel me-table-panel">
        <div className="table-wrap register-wrap">
          <table className="register-table dc-settings">
            <thead><tr><th>Code</th><th>Document Name</th><th>Group</th><th>Submission Type</th>{FLAGS.map(([, l]) => <th key={l}>{l}</th>)}
              {APPLY.map(([, l]) => <th key={l}>{l}</th>)}<th>Reminder (d)</th><th>Active</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || `new${i}`} className={!r.id || orig[r.id] !== JSON.stringify(r) ? 'dirty' : ''}>
                  <td><input className="dc-code" value={r.code || ''} disabled={!canEdit} onChange={ev => upd(i, 'code', ev.target.value)} /></td>
                  <td><input className="dc-name" value={r.name || ''} disabled={!canEdit} onChange={ev => upd(i, 'name', ev.target.value)} /></td>
                  <td><Select value={r.doc_group} disabled={!canEdit} title="Group" onChange={ev => upd(i, 'doc_group', ev.target.value)}>
                    {Object.entries(meta.groups).map(([g, t]) => <option key={g} value={g}>{g} · {t}</option>)}</Select></td>
                  <td><Select value={r.submission_type || ''} disabled={!canEdit} title="Submission Type" onChange={ev => upd(i, 'submission_type', ev.target.value)}>
                    {meta.types.map(t => <option key={t} value={t}>{t}</option>)}</Select></td>
                  {FLAGS.map(([k]) => <td key={k}><input type="checkbox" checked={!!r[k]} disabled={!canEdit} onChange={ev => upd(i, k, ev.target.checked)} /></td>)}
                  {APPLY.map(([k]) => <td key={k}><input className="dc-apply" value={r[k] || ''} placeholder="All" disabled={!canEdit} onChange={ev => upd(i, k, ev.target.value)} /></td>)}
                  <td><input className="dc-num" type="number" min={0} value={r.reminder_days ?? 0} disabled={!canEdit} onChange={ev => upd(i, 'reminder_days', ev.target.value === '' ? 0 : Number(ev.target.value))} /></td>
                  <td><input type="checkbox" checked={r.active !== false} disabled={!canEdit} onChange={ev => upd(i, 'active', ev.target.checked)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
